'use client'

import { Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

import type { Clarification, ClarificationQuestion } from '../stream/dataParts'

type ClarificationPartProps = {
  clarification: Clarification
  /** False once the conversation has moved past the questions or while a reply is streaming. */
  canAnswer: boolean
  /** May return a promise that settles once the answer has been sent. */
  onAnswer: (answer: string) => void | Promise<unknown>
}

type Answers = Record<string, string[]>

/**
 * Questions the assistant asked; the answers go back as the next user message. A lone single-choice question
 * answers on click. Otherwise each question takes its choices or typed text and one button sends them all.
 */
export default function ClarificationPart({ clarification, canAnswer, onAnswer }: ClarificationPartProps) {
  const t = useTranslations('features.AssistantV2')
  const [answers, setAnswers] = useState<Answers>({})
  // Locks the controls from the first submission, so a double click sends one message; the ref closes the gap
  // before the re-render lands. The lock holds while the parent has the turn (canAnswer false) and lifts when the
  // answer settles or the questions become answerable again.
  const submitting = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isDisabled = !canAnswer || isSubmitting
  const { questions } = clarification
  const [onlyQuestion] = questions
  const answersOnClick = questions.length === 1 && onlyQuestion?.type === 'single' && onlyQuestion.options.length > 0
  const isComplete = questions.every((question) => (answers[question.id] ?? []).some((answer) => answer.trim()))

  useEffect(() => {
    if (!canAnswer) return
    submitting.current = false
    setIsSubmitting(false)
  }, [canAnswer])

  const submit = (answer: string) => {
    if (submitting.current || !canAnswer) return
    submitting.current = true
    setIsSubmitting(true)
    const result = onAnswer(answer)
    if (!(result instanceof Promise)) return
    void result.finally(() => {
      submitting.current = false
      setIsSubmitting(false)
    })
  }

  const choose = (question: ClarificationQuestion, value: string) => {
    if (answersOnClick) {
      submit(value)
      return
    }
    setAnswers((current) => {
      const chosen = current[question.id] ?? []
      if (question.type !== 'multi') return { ...current, [question.id]: [value] }
      const next = chosen.includes(value) ? chosen.filter((item) => item !== value) : [...chosen, value]
      return { ...current, [question.id]: next }
    })
  }

  return (
    <Paper variant="outlined" data-testid="assistant-clarification" sx={{ p: 2 }}>
      <Stack spacing={2}>
        {clarification.intro && <Typography variant="body2">{clarification.intro}</Typography>}
        {questions.map((question) => (
          <Stack key={question.id} spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {question.prompt}
            </Typography>
            {question.options.length > 0 ? (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {question.options.map((option) => (
                  <Chip
                    key={option.id}
                    label={option.label}
                    clickable
                    disabled={isDisabled}
                    color={answers[question.id]?.includes(option.value) ? 'primary' : 'default'}
                    variant={answers[question.id]?.includes(option.value) ? 'filled' : 'outlined'}
                    onClick={() => choose(question, option.value)}
                  />
                ))}
              </Stack>
            ) : (
              <TextField
                size="small"
                disabled={isDisabled}
                value={answers[question.id]?.[0] ?? ''}
                onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: [event.target.value] }))}
                slotProps={{ htmlInput: { 'aria-label': question.prompt } }}
              />
            )}
          </Stack>
        ))}
        {!answersOnClick && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="contained"
              disabled={isDisabled || !isComplete}
              onClick={() => submit(formatAnswers(questions, answers))}
            >
              {t('sendAnswers')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

/** One question's answer is sent as is; several are sent one per line after their prompt. */
function formatAnswers(questions: ClarificationQuestion[], answers: Answers): string {
  const lines = questions.map((question) => ({
    prompt: question.prompt,
    answer: (answers[question.id] ?? []).map((answer) => answer.trim()).join(', ')
  }))
  if (lines.length === 1) return lines[0]?.answer ?? ''
  return lines.map(({ prompt, answer }) => `${prompt} ${answer}`).join('\n')
}
